/**
 * Result of normalizing text with position mapping.
 */
export interface NormalizedTextWithMap {
  /** The normalized text */
  norm: string;
  /**
   * Position map from normalized indices to original indices.
   * `map[i]` is the UTF-16 code unit index in the original string that produced `norm[i]`.
   */
  map: number[];
}

/**
 * Configuration options for text normalization.
 */
export interface NormalizationConfig {
  /**
   * Custom diacritic to base letter mapping.
   * Keys are lowercase diacritics, values are their base letter equivalents.
   * @default POLISH_DIACRITICS
   */
  diacriticMap?: Record<string, string>;
  /**
   * Whether to strip markdown markers (asterisks).
   * @default true
   */
  stripMarkdown?: boolean;
  /**
   * Whether to strip HTML tags.
   * @default true
   */
  stripHtml?: boolean;
  /**
   * Whether to collapse whitespace to single spaces.
   * @default true
   */
  collapseWhitespace?: boolean;
}

/**
 * Polish diacritics to base letter mapping for fuzzy matching.
 * This helps when LLM returns slightly different word forms.
 */
export const POLISH_DIACRITICS: Record<string, string> = {
  ą: 'a',
  ć: 'c',
  ę: 'e',
  ł: 'l',
  ń: 'n',
  ó: 'o',
  ś: 's',
  ź: 'z',
  ż: 'z',
};

/**
 * Default normalization configuration.
 */
const DEFAULT_CONFIG: Required<NormalizationConfig> = {
  diacriticMap: POLISH_DIACRITICS,
  stripMarkdown: true,
  stripHtml: true,
  collapseWhitespace: true,
};

/**
 * Create a character canonicalization function with the given configuration.
 *
 * @param config - Normalization configuration
 * @returns Function that canonicalizes a single character
 */
function createCanonicalizer(
  config: Required<NormalizationConfig>,
): (ch: string) => string {
  return function canonicalizeChar(ch: string): string {
    // Normalize spaces
    if (ch === '\u00a0') return ' '; // NBSP

    // Remove format-ish chars at char level by returning empty
    if (
      ch === '\u00ad' || // soft hyphen
      ch === '\u200b' || // zero-width space
      ch === '\u200c' || // zero-width non-joiner
      ch === '\u200d' || // zero-width joiner
      ch === '\ufeff' // BOM / zero-width no-break space
    ) {
      return '';
    }

    // Strip markdown formatting markers for flexible matching.
    // LLM may or may not include asterisks in quotes - stripping ensures matching works either way.
    // The map correctly points back to original positions for accurate highlighting.
    if (config.stripMarkdown && ch === '*') {
      return '';
    }

    // Strip curly braces (LLM sometimes uses {text} to indicate highlighted portions)
    if (ch === '{' || ch === '}') {
      return '';
    }

    // Normalize dashes/minus
    if (ch === '–' || ch === '—' || ch === '−') return '-';

    // Remove all quotation marks entirely for matching purposes.
    // LLM often adds quotes around citations that don't exist in the source text.
    // Removing them ensures matching works regardless of quote presence.
    if (
      ch === '\u201E' || // U+201E DOUBLE LOW-9 QUOTATION MARK (Polish opening „)
      ch === '\u201C' || // U+201C LEFT DOUBLE QUOTATION MARK (")
      ch === '\u201D' || // U+201D RIGHT DOUBLE QUOTATION MARK (")
      ch === '\u0022' || // U+0022 QUOTATION MARK (ASCII ")
      ch === '\u0027' || // U+0027 APOSTROPHE (ASCII ')
      ch === '\u2018' || // U+2018 LEFT SINGLE QUOTATION MARK (')
      ch === '\u2019' || // U+2019 RIGHT SINGLE QUOTATION MARK (')
      ch === '\u00AB' || // U+00AB LEFT-POINTING DOUBLE ANGLE QUOTATION MARK («)
      ch === '\u00BB' // U+00BB RIGHT-POINTING DOUBLE ANGLE QUOTATION MARK (»)
    ) {
      return '';
    }

    // Normalize diacritics to base letters for fuzzy matching
    // This handles cases where LLM returns "teofilina" but source has "teofiliną"
    const lower = ch.toLowerCase();
    if (lower in config.diacriticMap) {
      return config.diacriticMap[lower]!;
    }

    return ch;
  };
}

/**
 * Strip HTML tags from input text, returning the cleaned text and a mapping
 * from cleaned positions to original positions.
 *
 * Example: "<i>bold</i>" → { text: "bold", map: [3, 4, 5, 6] }
 * The map[0]=3 means cleaned[0] ('b') came from original[3].
 *
 * Special handling:
 * - <br> tags are converted to newlines (for line break preservation)
 * - All other tags are stripped entirely
 */
function stripHtmlTags(input: string): { text: string; map: number[] } {
  const out: string[] = [];
  const map: number[] = [];

  let i = 0;

  while (i < input.length) {
    const ch = input[i]!;

    if (ch === '<') {
      // Check for <br> or <br/> tags - convert to newline
      const remaining = input.slice(i);
      const brMatch = remaining.match(/^<br\s*\/?>/i);

      if (brMatch) {
        // Convert <br> to newline for proper text flow matching
        out.push('\n');
        map.push(i);
        i += brMatch[0].length;
        continue;
      }

      // Find closing '>' for other HTML tags
      const closeIdx = input.indexOf('>', i);
      if (closeIdx !== -1) {
        // Skip the entire tag
        i = closeIdx + 1;
        continue;
      }

      // No closing '>' found - treat '<' as literal character
      out.push(ch);
      map.push(i);
      i++;
      continue;
    }

    // Normal character outside HTML tags
    out.push(ch);
    map.push(i);
    i++;
  }

  return { text: out.join(''), map };
}

/**
 * Normalize text for fuzzy matching while preserving a mapping back to original indices.
 *
 * This function is the core of the highlighting system's text matching capability.
 * It transforms input text into a normalized form that allows matching between
 * LLM-generated quotes and source document content, even when there are minor
 * differences in formatting, diacritics, or whitespace.
 *
 * Normalization rules applied:
 * - Strip HTML tags (for matching HTML table content against plain text quotes)
 * - Unicode NFKC normalization + lowercase conversion
 * - Collapse whitespace runs to a single space
 * - Normalize NBSP to regular space
 * - Remove soft hyphens and zero-width characters
 * - Normalize dash variants (en-dash, em-dash, minus) to hyphen
 * - Remove quotation marks
 * - Normalize diacritics to base letters (configurable)
 *
 * @param input - The text to normalize
 * @param config - Optional configuration for normalization behavior
 * @returns Object containing normalized text and position mapping
 *
 * @example
 * ```typescript
 * const result = normalizeWithMap('Teofiliną – stosować ostrożnie');
 * // result.norm = 'teofilina - stosowac ostroznie'
 * // result.map contains indices mapping back to original positions
 * ```
 */
export function normalizeWithMap(
  input: string,
  config?: NormalizationConfig,
): NormalizedTextWithMap {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const canonicalizeChar = createCanonicalizer(mergedConfig);

  // First pass: strip HTML tags and get position mapping
  const { text: htmlStripped, map: htmlMap } = mergedConfig.stripHtml
    ? stripHtmlTags(input)
    : { text: input, map: Array.from({ length: input.length }, (_, i) => i) };

  const out: string[] = [];
  const map: number[] = [];

  let pendingSpace = false;
  let pendingSpaceIndex = -1;

  let i = 0; // Index in htmlStripped
  for (const raw of htmlStripped) {
    const rawLen = raw.length; // 1 or 2
    const canon = canonicalizeChar(raw);

    if (canon === '') {
      i += rawLen;
      continue;
    }

    const normalized = canon.normalize('NFKC').toLowerCase();

    // Collapse whitespace
    if (mergedConfig.collapseWhitespace && /\s/.test(normalized)) {
      if (!pendingSpace) {
        pendingSpace = true;
        // Map back to original position through htmlMap
        pendingSpaceIndex = htmlMap[i] ?? i;
      }
      i += rawLen;
      continue;
    }

    // Flush pending space
    if (pendingSpace) {
      out.push(' ');
      map.push(pendingSpaceIndex);
      pendingSpace = false;
      pendingSpaceIndex = -1;
    }

    // Append (normalized can be multiple codepoints)
    for (const ch of normalized) {
      out.push(ch);
      // Map back to original position through htmlMap
      map.push(htmlMap[i] ?? i);
    }

    i += rawLen;
  }

  const normStr = out.join('');

  // Trim leading/trailing spaces while keeping mapping aligned
  let start = 0;
  while (start < normStr.length && normStr[start] === ' ') start++;

  let end = normStr.length;
  while (end > start && normStr[end - 1] === ' ') end--;

  return {
    norm: normStr.slice(start, end),
    map: map.slice(start, end),
  };
}
